import * as React from 'react';

/**
 * Formats a string according to the pattern.
 * @example
 * convertToPatternLikeValue("123", "9-99-9", true)   // -> "*-**"
 * convertToPatternLikeValue("1234", "9-99-9", true)  // -> "*-**-*"
 * convertToPatternLikeValue("123", "9-99-9", false)  // -> "1-23"
 * convertToPatternLikeValue("123", "9 9 9 9", false) // -> "1 2 3"
 */
export function convertToPatternLikeValue(value: string, pattern: string, hideValue: boolean): string {
  let valueIndex = 0;
  let patternIndex = 0;
  let displayValue = '';

  while (valueIndex < value.length) {
    const patternChar = pattern[patternIndex];
    if (patternChar === undefined) {
      break;
    }

    const isNormalValueChar = ['*', '9', 'a'].includes(patternChar);

    if (isNormalValueChar && hideValue) {
      displayValue += '*';
    } else if (isNormalValueChar && !hideValue) {
      displayValue += value[valueIndex];
    } else {
      displayValue += patternChar;
    }
    patternIndex++;

    if (isNormalValueChar) {
      valueIndex++;
    }
  }
  return displayValue;
}

/**
 * Removes all format chars that don't represent values
 * @example
 * filterNonSensitiveFromValue("9-9-9") // -> "999"
 * filterNonSensitiveFromValue("a-a*")  // -> "aa*"
 */
function filterNonSensitiveFromValue(str: string) {
  return str.replace(/[^*|9|a]/gi, '');
}

const REGEX = {
  DIGIT: new RegExp(/^[0-9]*$/),
  // todo: add a-z
};

/**
 * Removes all chars that are not allowed.
 * @example
 * stripNotAllowedText("123") // -> "123"
 * stripNotAllowedText("ab123") // -> "123"
 * stripNotAllowedText("1a2b3c") // -> "123"
 * stripNotAllowedText("abc") // -> ""
 * TODO: handle different types of chars (alpha, alphanumeric, numeric) at different indexes.
 */
function stripNotAllowedText(value: string) {
  let result = '';
  // eslint-disable-next-line no-restricted-syntax
  for (const i of value) {
    if (REGEX.DIGIT.test(i)) {
      result += i;
    }
  }
  return result;
}

/**
 * Removes not allowed characters and returns string that doesn't exceed the max length
 * No formatting is applied to the result string.
 * @example
 * normalizeInitialValue("123", "9-9") // -> "12"
 * normalizeInitialValue("1-2", "9-9") // -> "12"
 */
export function normalizeInitialValue(value: string, pattern: string) {
  const valueMaxLen = filterNonSensitiveFromValue(pattern).length;
  const strippedText = stripNotAllowedText(value);
  return strippedText.slice(0, valueMaxLen);
}

type MaskParameters = {
  pattern: string;
  revealedValue: string;
  inputRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement>;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, value?: string) => void;
};

export function useMask({ pattern, revealedValue, inputRef, onChange: clientOnChange }: MaskParameters) {
  const selectionRefBeforeChange = React.useRef<{ start: number | null; end: number | null }>({
    start: null,
    end: null,
  });
  const selectionRefAfterChange = React.useRef<number | null>(null);

  /**
   * Meaningful revealed input value without any format related characters.
   * For example, for formatting "***-***-***" and value in controller "123-400-1"
   * this variabled value would be "1234001"
   */
  const value = normalizeInitialValue(revealedValue, pattern);
  /**
   * Value formatted for the text input.
   * For value in controller "123-400-1", displayValue would be "***-***-*"
   */
  const displayValue = convertToPatternLikeValue(value, pattern, true);

  /**
   * This method updates the caret position that will be later used in `onChange` callback
   * It's triggered by both mouse and keyboard caret update.
   */
  const onSelectionChange = React.useCallback(() => {
    if (inputRef.current === document.activeElement) {
      selectionRefBeforeChange.current = {
        start: inputRef.current.selectionStart,
        end: inputRef.current.selectionEnd,
      };
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, []);

  /**
   * This method returns the start and end caret position.
   */
  function getStartEnd(): { start: number; end: number; isSelectionChange: boolean } {
    const { start, end } = selectionRefBeforeChange.current;
    const currentSelection = {
      start: inputRef.current.selectionStart!,
      end: inputRef.current.selectionEnd!,
    };

    // detect user selection
    if (start !== end) {
      return {
        start: start!,
        end: end!,
        isSelectionChange: true,
      };
    } else {
      return {
        start: start!,
        end: currentSelection.end,
        isSelectionChange: false,
      };
    }
  }

  function isDeleteOperation(event: React.ChangeEvent<HTMLInputElement>): boolean {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { inputType } = event.nativeEvent;
    if (typeof inputType === 'string') {
      // the full list of options https://rawgit.com/w3c/input-events/v1/index.html#interface-InputEvent-Attributes
      return inputType.startsWith('delete');
    } else {
      // IE11 and Edge based on IE11
      return event.target.value.length < displayValue.length;
    }
  }

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line prefer-const
    let { start, end, isSelectionChange } = getStartEnd();
    const deleteOperation = isDeleteOperation(event);

    const isForwardDeletePress = deleteOperation && start === end;

    // when pressing forward delete start and end are equal
    if (isForwardDeletePress) {
      end += 1;
    }

    // calculate the string value maximum length
    const maxLenByPattern = filterNonSensitiveFromValue(pattern).length;

    const min = Math.min(start, end);
    const max = Math.max(start, end);

    // text value entered by user
    const newValue = deleteOperation ? '' : inputRef.current.value.slice(min, max);
    const newValueStripped = stripNotAllowedText(newValue);

    // here we are calculating the text value **before** user input
    const beforeMasked = displayValue.slice(0, min);
    const beforeRevealedLen = filterNonSensitiveFromValue(beforeMasked).length;
    const beforeValue = value.slice(0, beforeRevealedLen);

    // here we are calculating the text value **after** user input
    const keepValueOnSameIndex = !deleteOperation && !isSelectionChange;
    const upToEndIndex = keepValueOnSameIndex ? min : max;
    const upToNewMasked = displayValue.slice(0, upToEndIndex);
    const upToNewRevealedLen = filterNonSensitiveFromValue(upToNewMasked).length;
    const afterValue = value.slice(upToNewRevealedLen, value.length);

    const combinedString = beforeValue + newValueStripped + afterValue;

    const result = combinedString.slice(0, maxLenByPattern);

    // We need to send formatted + revealed value to keep the same behavior (same format)
    // as we have with the react-input-mask (not password like)
    const formattedResult = convertToPatternLikeValue(result, pattern, false);

    // Send the updated value (formatted + revealed) to the controller
    clientOnChange(event, formattedResult);

    const textBeforeCaret = convertToPatternLikeValue(
      (beforeValue + newValueStripped).slice(0, maxLenByPattern),
      pattern,
      true
    );

    // if a user input event has no change to the input value
    const wrongTextChange = !deleteOperation && newValueStripped.length === 0;

    const nextCaretPosition = wrongTextChange ? start : textBeforeCaret.length;

    selectionRefAfterChange.current = nextCaretPosition;

    setTimeout(() => {
      // restore caret position
      // TODO: investigate how to prevent cursor moving (hiding cursor with CSS, preventing a change, etc.)
      inputRef.current.selectionStart = selectionRefAfterChange.current;
      inputRef.current.selectionEnd = selectionRefAfterChange.current;
    });
  };

  return {
    value,
    displayValue,
    // The input value formatted and always revealed
    formattedValue: convertToPatternLikeValue(value, pattern, false),
    onChange,
    onSelect: onSelectionChange,
  };
}
